import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'clsx';
import {
  Section,
  SectionSubTitle,
  SwiperWithNavigation,
  SwiperSlide,
  LinkWithGAPromoClick,
  Typography,
} from 'common/components';
import { KAZAKHSTAN } from 'common/constants/region-codes';
import { usePromoViewEvent } from 'common/hooks';
import { Landing } from 'modules/landings-info/landings-info-types';
import { ServiceCategory } from 'modules/service-categories/service-categories-types';
import { createElementKey } from 'utils';
import { analyticsUtils, ClickButtonElementName } from 'utils/analytics-utils';
import { getFinalServicesData } from 'landings/services-main/services-main-utils';
import { KZ_POPULAR_SERVICES_LIST, POPULAR_SERVICES_LIST } from './services-popular-section-constants';
import {
  popularSwiper,
  popularSlide,
  popularItem,
  allPopularItem,
  popularItemImgWrapper,
  popularItemImage,
  popularItemText,
} from './services-popular-section-styles';

type ServicesPopularSectionProps = {
  landingsList: Landing[] | null;
  serviceCategoriesList: ServiceCategory[] | null;
  regionId: string;
};

export const ServicesPopularSection = ({
  landingsList,
  serviceCategoriesList,
  regionId,
}: ServicesPopularSectionProps) => {
  const { t } = useTranslation();
  // Получаем список доступных в регионе популярных услуг
  const availablePopularServices = getFinalServicesData(
    regionId === KAZAKHSTAN ? KZ_POPULAR_SERVICES_LIST : POPULAR_SERVICES_LIST,
    landingsList,
    serviceCategoriesList,
  );

  const refs = useRef<HTMLAnchorElement[]>([]);
  const servicesPromotions = useMemo(
    () =>
      availablePopularServices.map((service, index) => ({
        name: 'servicePage_top',
        creative: service.serviceAddName || service.serviceName,
        position: (index + 1).toString(),
      })),
    [availablePopularServices]
  );

  const promotions = useMemo(
    () => [
      ...servicesPromotions,
      {
        name: 'servicePage_top',
        creative: 'Смотреть всё',
        position: (servicesPromotions.length + 1).toString(),
      },
    ],
    [servicesPromotions]
  );

  usePromoViewEvent(refs, 'servicePage / popular', promotions);

  /**
   * Обработчик нажатия кнопки "Следующий слайд"
   */
  const handleNextSlideBtnClick = () => {
    analyticsUtils.clickButtonEvent(
      'servicePage / popular',
      ClickButtonElementName.SwitchForward,
      'Популярные услуги'
    );
  };

  /**
   * Обработчик нажатия кнопки "Предыдущий слайд"
   */
  const handlePrevSlideBtnClick = () => {
    analyticsUtils.clickButtonEvent(
      'servicePage / popular',
      ClickButtonElementName.SwitchBackward,
      'Популярные услуги'
    );
  };

  return (
    <Section>
      <SectionSubTitle adaptiveMargin margin="m" variant="h3">
        {t('popular-section.title')}
      </SectionSubTitle>
      <SwiperWithNavigation
        className={popularSwiper}
        slidesPerView="auto"
        onSlideNextTransitionEnd={handleNextSlideBtnClick}
        onSlidePrevTransitionEnd={handlePrevSlideBtnClick}>
        {availablePopularServices.map((service, index) => (
          <SwiperSlide className={popularSlide} key={createElementKey('popular-service', index)}>
            <LinkWithGAPromoClick
              className={popularItem}
              title={service.serviceAddName || service.serviceName}
              href={service.serviceUrl}
              elementBlock="servicePage / popular"
              position={index}
              promotion={promotions[index]}
              bannerRefs={refs}>
              <div className={popularItemImgWrapper}>
                <img
                  className={popularItemImage}
                  src={service.serviceImage || ''}
                  alt={service.serviceAddName || service.serviceName}
                  title={service.serviceAddName || service.serviceName}
                  width="220"
                  height="128"
                />
              </div>
              <Typography className={popularItemText} variant="m" size="subtitle" adaptive={false}>
                {service.serviceAddName?.replace('под ', 'под\u00A0').replace(' и рекуператоров', '')}
              </Typography>
            </LinkWithGAPromoClick>
          </SwiperSlide>
        ))}
        {regionId !== KAZAKHSTAN && (
          <SwiperSlide className={popularSlide}>
            <LinkWithGAPromoClick
              className={cn(popularItem, allPopularItem)}
              title=""
              href="/uslugi/ustanovka-i-remont/"
              elementBlock="servicePage / popular"
              position={availablePopularServices.length}
              promotion={promotions[availablePopularServices.length]}
              bannerRefs={refs}>
              <Typography variant="m" size="body_short">
                {t('popular-section.show-all-button')}
              </Typography>
            </LinkWithGAPromoClick>
          </SwiperSlide>
        )}
      </SwiperWithNavigation>
    </Section>
  );
};
